<template>
    <div>
        <button
            @click="onClick"
            :name="`add_to_cart-${product.id}`"
            type="button"
            class="w-full mt-1 py-2 shadow-sm rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 focus:outline-none"
        >
            <span class="flex items-center justify-center">
                <LoadingIcon v-if="loading" class="h-4 w-4 mr-2" />
                <CheckIcon
                    v-else-if="added"
                    class="h-4 w-4 mr-2"
                    aria-hidden="true"
                />
                <ShoppingCartIcon
                    v-else
                    class="h-4 w-4 mr-2"
                    aria-hidden="true"
                />
                {{ added ? "Added to Cart" : "Add to Cart" }}
            </span>
        </button>

        <p
            v-if="error"
            class="flex justify-center items-center text-sm text-red-600 my-2"
        >
            <XCircleIcon class="h-4 w-4 mr-2 text-red-500" aria-hidden="true" />
            {{ error }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { CheckIcon, ShoppingCartIcon, XCircleIcon } from "@heroicons/vue/solid";
import type { AppEmitter } from "../../emitter";
import LoadingIcon from "../LoadingIcon.vue";
import { useAddToCart } from "../../functions/useAddToCart";

// Adds the default option, because this button stands where there is nothing to pick one with.
// Somebody who wants another size follows the title beside it to the product page.
const props = defineProps<{
    product?: any;
    source?: string;
}>();

const emitter = inject<AppEmitter>("mitt")!;

// The side bar opens on success: this button sits on a page about a review rather than about
// shopping, where a label change alone leaves it unclear that anything reached the cart.
const { loading, added, error, addToCart } = useAddToCart(
    props.source ?? "",
    openCartSideBar,
);

// Once the item is in, the button shows the cart again rather than adding a second one — a
// control still reading "Added to Cart" must not quietly raise the quantity when pressed.
function onClick(): void {
    if (added.value) {
        openCartSideBar();

        return;
    }

    addToCart(props.product);
}

function openCartSideBar(): void {
    emitter.emit("openCartSideBar");
}
</script>
